
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Package, User, MapPin, CreditCard, Printer, FileText } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";
import { useReceiptCustomization } from "@/hooks/useReceiptCustomization";
import CustomerSignatureManager from "./CustomerSignatureManager";

interface EditablePrintPreviewProps {
  order: EtsyOrder;
  onPrint: (editedOrder: EtsyOrder, supplierNotes: string, customTitle: string) => void;
  onCancel: () => void;
}

const EditablePrintPreview = ({ order, onPrint, onCancel }: EditablePrintPreviewProps) => {
  const [editedOrder, setEditedOrder] = useState<EtsyOrder>(order);
  const [supplierNotes, setSupplierNotes] = useState("");
  const { customization, updateTitle } = useReceiptCustomization(order.receiptId);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Processing":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      case "Shipped":
        return "bg-green-50 text-green-700 border-green-200";
      case "Delivered":
        return "bg-gray-50 text-gray-700 border-gray-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const handlePrint = () => {
    onPrint(editedOrder, supplierNotes, customization.title);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Edit Print Preview</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            Print Now
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="text-center border-b">
          <div className="space-y-3">
            <div>
              <Label htmlFor="receiptTitle" className="text-sm font-medium flex items-center gap-2 justify-center mb-2">
                <FileText className="h-4 w-4" />
                Receipt Title
              </Label>
              <Input
                id="receiptTitle"
                value={customization.title}
                onChange={(e) => updateTitle(e.target.value)}
                className="text-center text-xl font-bold border-dashed"
                placeholder="Order Summary"
              />
            </div>
            <div className="flex justify-center items-center gap-2">
              <Badge variant="outline" className={getStatusColor(editedOrder.status)}>
                {editedOrder.status}
              </Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* Order Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Order Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="receiptId">Order ID</Label>
                <Input
                  id="receiptId"
                  value={editedOrder.receiptId}
                  onChange={(e) => setEditedOrder({...editedOrder, receiptId: e.target.value})}
                  className="font-mono"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  value={editedOrder.date}
                  onChange={(e) => setEditedOrder({...editedOrder, date: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="productName">Product</Label>
                <Input
                  id="productName"
                  value={editedOrder.productName}
                  onChange={(e) => setEditedOrder({...editedOrder, productName: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="trackingId">Tracking Number</Label>
                <Input
                  id="trackingId"
                  value={editedOrder.trackingId || ''}
                  onChange={(e) => setEditedOrder({...editedOrder, trackingId: e.target.value})}
                  placeholder="Enter tracking number"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Customer Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="customerName">Name</Label>
                <Input
                  id="customerName"
                  value={editedOrder.customerName}
                  onChange={(e) => setEditedOrder({...editedOrder, customerName: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="customerEmail">Email</Label>
                <Input
                  id="customerEmail"
                  value={editedOrder.customerEmail || ''}
                  onChange={(e) => setEditedOrder({...editedOrder, customerEmail: e.target.value})}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Shipping Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Shipping Address
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={editedOrder.address || ''}
                  onChange={(e) => setEditedOrder({...editedOrder, address: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={editedOrder.city}
                    onChange={(e) => setEditedOrder({...editedOrder, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="zip">Zip Code</Label>
                  <Input
                    id="zip"
                    value={editedOrder.zip}
                    onChange={(e) => setEditedOrder({...editedOrder, zip: e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Payment Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="totalAmount">Total Amount</Label>
                <div className="flex gap-2">
                  <Input
                    id="currencyCode"
                    value={editedOrder.currencyCode || ''}
                    onChange={(e) => setEditedOrder({...editedOrder, currencyCode: e.target.value})}
                    placeholder="USD"
                    className="w-20"
                  />
                  <Input
                    id="totalAmount"
                    type="number"
                    step="0.01"
                    value={editedOrder.totalAmount || ''}
                    onChange={(e) => setEditedOrder({...editedOrder, totalAmount: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <Label>Payment Method</Label>
                <div className="pt-2 text-sm">Etsy Payment</div>
              </div>
            </div>
          </div>

          {/* Supplier Notes Section */}
          <Separator />
          <div>
            <Label htmlFor="supplierNotes">Supplier Notes</Label>
            <Textarea
              id="supplierNotes"
              value={supplierNotes}
              onChange={(e) => setSupplierNotes(e.target.value)}
              placeholder="Add any special instructions or notes for the supplier..."
              rows={4}
              className="mt-2"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer Signature Manager */}
      <CustomerSignatureManager 
        customerName={editedOrder.customerName}
        receiptId={editedOrder.receiptId}
      />
    </div>
  );
};

export default EditablePrintPreview;
